import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import logo from '../../assets/images/logo.svg';
import { getKycInfo, KycError, KycInfo, KycReceiver, KycSide, lookupPin, submitKyc } from '../../api/kyc';
import {
  Check,
  checkAadhaar,
  checkAddress,
  checkIqama,
  checkMobile,
  checkName,
  checkOtherNumber,
  checkPin,
  COUNTRIES,
  digits,
  formatAadhaar,
  formatIqama,
  formatMobile,
  formatName,
  formatOtherNumber,
  formatPin,
  ifTyped,
  required,
} from './validate';
import CropDialog from './CropDialog';
import { SampleCard } from './SampleCard';
import SearchSelect, { Option } from './SearchSelect';
import { plainKey, SHORTCUTS, typing, windowOpen } from './keys';
import INDIA_LIST from './india.json';
import styles from './Kyc.module.css';

/** One entry per dial code (the first country that uses it), searchable by country or code. */
const DIAL_OPTIONS: Option[] = COUNTRIES.filter((c, i, all) => all.findIndex((x) => x.dial === c.dial) === i).map((c) => ({
  value: c.dial,
  label: `${c.flag} ${c.name}`,
  hint: `+${c.dial}`,
  search: `${c.code} +${c.dial}`,
}));
const COUNTRY_OPTIONS: Option[] = COUNTRIES.map((c) => ({ value: c.name, label: `${c.flag} ${c.name}`, search: c.code }));
const ID_OPTIONS: Option[] = ['Passport', 'National ID card', 'Driving licence', 'Residence permit'].map((n) => ({ value: n, label: n }));
/** India's states and their districts (Aadhaar is India only, so the country is fixed). */
const INDIA = INDIA_LIST as Record<string, string[]>;
const STATE_OPTIONS: Option[] = Object.keys(INDIA).map((st) => ({ value: st, label: st }));
/** India Post / old names → our list ("Keralam" → "Kerala", "Delhi (NCT)" → "Delhi"). */
function matchState(name: string | null | undefined): string | null {
  if (!name) return null;
  const k = name.toLowerCase().replace(/[^a-z]/g, '');
  return Object.keys(INDIA).find((st) => {
    const s2 = st.toLowerCase().replace(/[^a-z]/g, '');
    return s2 === k || k.startsWith(s2) || s2.startsWith(k);
  }) ?? name;
}

const HELP = '+966 53 288 9245';
const MAX_BYTES = 25 * 1024 * 1024;

type Photo = { blob: Blob; url: string } | null;
type DocForm = {
  number: string;
  name: string;
  address: string;
  code: string;
  mobile: string;
  front: Photo;
  back: Photo;
};
type AadhaarForm = DocForm & { pin: string; area: string; district: string; state: string };
type OtherForm = DocForm & { docName: string; country: string };
/**
 * A box's receiver. Aadhaar first; left empty, Continue moves on to another
 * ID (`skipped` shows why). Both forms are kept so switching back loses nothing.
 */
type Receiver = { kind: 'aadhaar' | 'other'; skipped: boolean; ad: AadhaarForm; ot: OtherForm };

const Tick: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} aria-hidden>
    <path d="M5 12l5 5L20 7" />
  </svg>
);

const CardIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <rect x="2" y="5" width="20" height="14" rx="3" />
    <circle cx="8" cy="11" r="2" />
    <path d="M5 16c.8-1.8 5.2-1.8 6 0M14 10h5M14 14h3" />
  </svg>
);

const Kbd: React.FC<{ k: string }> = ({ k }) => <kbd className={styles.kbd}>{k}</kbd>;

/** One side of a card: tap (or its key) to take a photo or choose one; shows the photo once added. */
const PhotoSlot: React.FC<{
  label: string;
  kind: 'iqama' | 'aadhaar' | 'other';
  front: boolean;
  needed: boolean;
  photo: Photo;
  missing: boolean;
  onPhoto: (p: Photo) => void;
}> = ({ label, kind, front, needed, photo, missing, onPhoto }) => {
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // The picked photo waits in the crop window until the customer fits the card in the frame.
  const [cropping, setCropping] = useState<File | null>(null);
  const [viewing, setViewing] = useState(false);
  const hotkey = front ? 'f' : 'b';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!plainKey(e, hotkey)) return;
      e.preventDefault();
      input.current?.click();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hotkey]);

  // The large view: Enter = looks good, Esc = close.
  useEffect(() => {
    if (!viewing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        e.preventDefault();
        setViewing(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing]);

  function pick(file: File | undefined) {
    if (!file) return;
    if (file.size > MAX_BYTES) return setProblem('This photo is too large — 25 MB at most');
    setProblem(null);
    setCropping(file);
  }
  const chooser = (
    <input
      ref={input}
      type="file"
      accept="image/*,.heic,.heif"
      hidden
      onChange={(e) => {
        pick(e.target.files?.[0]);
        e.target.value = '';
      }}
    />
  );
  const crop = cropping && (
    <CropDialog
      file={cropping}
      title={label}
      onCancel={() => setCropping(null)}
      onDone={(blob) => {
        setCropping(null);
        if (photo) URL.revokeObjectURL(photo.url);
        onPhoto({ blob, url: URL.createObjectURL(blob) });
      }}
    />
  );
  const title = (
    <b>
      {label} <span className={`${styles.tag} ${needed ? styles.tagNeed : ''}`}>{needed ? 'Needed' : 'Optional'}</span>
      <Kbd k={hotkey.toUpperCase()} />
    </b>
  );

  // Empty: the whole box opens the camera / gallery.
  if (!photo) {
    return (
      <>
        {crop}
        <button
          type="button"
          className={`${styles.up} ${missing && needed ? styles.upMissing : ''}`}
          onClick={() => input.current?.click()}
        >
          {chooser}
          <SampleCard kind={kind} front={front} />
          <span className={styles.upText}>
            {title}
            <span>
              {problem ??
                (missing && needed ? 'Photo needed' : needed ? 'Like this sample — take a photo or upload' : 'Add it if you have it')}
            </span>
          </span>
        </button>
      </>
    );
  }

  // Added: tap the photo to see it large; Re-upload to replace it.
  return (
    <>
      {crop}
      {viewing && (
        <div className={styles.cropScrim} role="dialog" aria-modal="true" aria-label={label} onClick={() => setViewing(false)}>
          <div className={styles.cropBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.cropHead}>
              <div>
                <h3>{label}</h3>
                <p>Check the card is clear and every number can be read</p>
              </div>
              <button type="button" className={styles.cropX} onClick={() => setViewing(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className={styles.cropStage}>
              <img src={photo.url} alt={label} className={styles.cropImg} />
            </div>
            <div className={styles.cropFoot}>
              <span className={styles.cropGap} />
              <button type="button" className={`${styles.btn} ${styles.gh}`} onClick={() => { setViewing(false); input.current?.click(); }}>
                Re-upload
              </button>
              <button type="button" className={styles.btn} onClick={() => setViewing(false)}>
                Looks good <Kbd k="Enter" />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`${styles.up} ${styles.upDone}`}>
        {chooser}
        <button type="button" className={styles.shotBtn} onClick={() => setViewing(true)} aria-label={`View ${label}`}>
          <img src={photo.url} alt={label} className={styles.shot} />
          <span className={styles.zoom}>🔍 View</span>
        </button>
        <span className={styles.upText}>
          {title}
          <span>{problem ?? 'Added'}</span>
          <span className={styles.upActions}>
            <button type="button" onClick={() => setViewing(true)}>View</button>
            <button type="button" onClick={() => input.current?.click()}>Re-upload</button>
          </span>
        </span>
        <span className={styles.ck}>
          <Tick size={12} />
        </span>
      </div>
    </>
  );
};

/** Label + input + the line under it (hint, error, warning or ✓). `need` marks a must-fill field; others say "optional". */
const Field: React.FC<{
  label: string;
  need?: boolean;
  check?: Check;
  show: boolean;
  hint?: string;
  wide?: boolean;
  children: React.ReactNode;
}> = ({ label, need, check = {}, show, hint, wide, children }) => {
  const state = show && check.error ? 'bad' : check.warn && show ? 'warn' : check.ok ? 'good' : '';
  const line = (show && (check.error || check.warn)) || check.ok || hint || '';
  return (
    <label className={`${styles.f} ${wide ? styles.wide : ''} ${state ? styles[state] : ''}`}>
      <span className={styles.lbl}>
        {label}
        {need ? <span className={styles.req}>*</span> : <span className={styles.opt}>optional</span>}
      </span>
      {children}
      <span className={styles.msg}>{line}</span>
    </label>
  );
};

const PhoneField: React.FC<{
  code: string;
  value: string;
  check: Check;
  show: boolean;
  onCode: (c: string) => void;
  onChange: (v: string) => void;
  onBlur: () => void;
}> = ({ code, value, check, show, onCode, onChange, onBlur }) => (
  <Field label="Mobile" check={check} show={show}>
    <span className={styles.tel}>
      <SearchSelect
        value={code}
        options={DIAL_OPTIONS}
        onChange={onCode}
        placeholder="Code"
        searchPlaceholder="Search country or code"
        ariaLabel="Country code"
        className={styles.telCode}
        display={(o) => `${o.label.split(' ')[0]} +${o.value}`}
      />
      <input inputMode="numeric" value={value} onChange={(e) => onChange(formatMobile(code, e.target.value))} onBlur={onBlur} />
    </span>
  </Field>
);

const blankDoc = (code: string): DocForm => ({ number: '', name: '', address: '', code, mobile: '', front: null, back: null });
const blankReceiver = (): Receiver => ({
  kind: 'aadhaar',
  skipped: false,
  ad: { ...blankDoc('91'), pin: '', area: '', district: '', state: '' },
  ot: { ...blankDoc('971'), docName: 'Passport', country: '' },
});

// The checks — only the number and the photos are needed; what else is typed must be right.
const iqChecksOf = (f: DocForm) => ({
  number: checkIqama(f.number),
  name: ifTyped(f.name, checkName(f.name)),
  address: ifTyped(f.address, checkAddress(f.address)),
  mobile: ifTyped(f.mobile, checkMobile(f.code, f.mobile)),
});
const adChecksOf = (f: AadhaarForm) => ({
  number: checkAadhaar(f.number),
  name: ifTyped(f.name, checkName(f.name)),
  address: ifTyped(f.address, checkAddress(f.address)),
  mobile: ifTyped(f.mobile, checkMobile(f.code, f.mobile)),
  pin: ifTyped(f.pin, checkPin(f.pin)),
});
const otChecksOf = (f: OtherForm) => ({
  docName: required(f.docName, 'which ID it is'),
  number: checkOtherNumber(f.number),
  name: ifTyped(f.name, checkName(f.name)),
  address: ifTyped(f.address, checkAddress(f.address)),
  mobile: ifTyped(f.mobile, checkMobile(f.code, f.mobile)),
});
const clean = (c: Record<string, Check>) => Object.values(c).every((x) => !x.error);
const receiverOk = (r: Receiver) =>
  r.kind === 'aadhaar' ? clean(adChecksOf(r.ad)) && !!r.ad.front && !!r.ad.back : clean(otChecksOf(r.ot)) && !!r.ot.front;
/** Nothing of the Aadhaar given yet — Continue then asks for another ID instead. */
const aadhaarEmpty = (r: Receiver) => !digits(r.ad.number) && !r.ad.front && !r.ad.back;

/** "2345 6789 4410" → "•••• •••• 4410". */
const masked = (n: string) => (digits(n).length > 4 ? `•••• •••• ${digits(n).slice(-4)}` : n);

/** The customer's KYC page: opened from the WhatsApp link, no login, submits once. */
const Kyc: React.FC = () => {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<KycInfo | null>(null);
  const [badLink, setBadLink] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [iq, setIq] = useState<DocForm>(() => blankDoc('966'));
  const [rs, setRs] = useState<Receiver[]>(() => [blankReceiver()]);
  /** Which box's receiver step 2 shows. */
  const [box, setBox] = useState(0);
  const [applyAll, setApplyAll] = useState(false);
  const [areas, setAreas] = useState<string[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [tried, setTried] = useState<Record<string, boolean>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [keysOpen, setKeysOpen] = useState(false);

  useEffect(() => {
    document.title = 'Complete your KYC | Sahari Cargo';
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.appendChild(robots);
    return () => robots.remove();
  }, []);

  useEffect(() => {
    getKycInfo(token)
      .then((i) => {
        setInfo(i);
        if (i.iqama) {
          setIq((f) => ({
            ...f,
            name: formatName(i.iqama!.name ?? ''),
            address: i.iqama!.address ?? '',
            code: i.iqama!.mobile.code || '966',
            mobile: formatMobile(i.iqama!.mobile.code || '966', i.iqama!.mobile.number),
          }));
        }
        // One receiver per box; the booking's receiver fills in the first.
        const list = Array.from({ length: Math.max(1, i.boxes) }, blankReceiver);
        if (i.aadhaar) {
          const a = i.aadhaar;
          const code = a.mobile.code || '91';
          const person = { name: formatName(a.name ?? ''), address: a.address ?? '', code, mobile: formatMobile(code, a.mobile.number) };
          list[0] = {
            ...list[0],
            ad: { ...list[0].ad, ...person, pin: formatPin(a.pin ?? ''), district: a.district ?? '', state: matchState(a.state) ?? '' },
            ot: { ...list[0].ot, ...person, code: code === '91' ? list[0].ot.code : code },
          };
        }
        setRs(list);
      })
      .catch((e) => setBadLink(e instanceof KycError && e.status === 404 ? 'This link is not valid.' : 'Could not open the page — please try again.'));
  }, [token]);

  const n = rs.length;
  const cur = rs[box] ?? rs[0];
  const boxName = (i: number) => info?.boxNumbers?.[i] ?? `Box ${i + 1}`;
  const setCur = (fn: (r: Receiver) => Receiver) => setRs((list) => list.map((r, i) => (i === box ? fn(r) : r)));
  const setAd = (fn: (f: AadhaarForm) => AadhaarForm) => setCur((r) => ({ ...r, ad: fn(r.ad) }));
  const setOt = (fn: (f: OtherForm) => OtherForm) => setCur((r) => ({ ...r, ot: fn(r.ot) }));

  // PIN → state, district and its areas (after the 6th digit), for the box on screen.
  const pin = cur.ad.pin;
  useEffect(() => {
    if (checkPin(pin).error || !info || info.state !== 'open') return setAreas([]);
    const t = setTimeout(() => {
      lookupPin(token, pin)
        .then((p) => {
          setAreas(p.localities ?? []);
          setRs((list) =>
            list.map((r, i) =>
              i !== box || r.ad.pin !== pin
                ? r
                : {
                    ...r,
                    ad: {
                      ...r.ad,
                      state: matchState(p.state) ?? r.ad.state,
                      district: p.district ?? r.ad.district,
                      area: (p.localities ?? []).includes(r.ad.area) ? r.ad.area : '',
                    },
                  },
            ),
          );
        })
        .catch(() => setAreas([]));
    }, 400);
    return () => clearTimeout(t);
  }, [pin, box, token, info]);

  const triedKey = step === 2 ? `r${box}` : String(step);
  const show = (k: string) => !!touched[k] || !!tried[triedKey];
  const blur = (k: string) => () => setTouched((t) => ({ ...t, [k]: true }));

  const iqChecks = iqChecksOf(iq);
  const adChecks = adChecksOf(cur.ad);
  const otChecks = otChecksOf(cur.ot);
  const iqOk = clean(iqChecks) && !!iq.front;
  const curOk = receiverOk(cur);
  const lastBox = applyAll || box === n - 1;
  /** A box's chip can be opened once every box before it has its receiver. */
  const reachable = (i: number) => !applyAll && rs.slice(0, i).every(receiverOk);

  function go(s: number, b = box) {
    setError(null);
    setStep(s);
    setBox(b);
    window.scrollTo({ top: 0 });
  }

  function next() {
    setError(null);
    if (step === 1) return iqOk ? go(2, 0) : setTried((t) => ({ ...t, 1: true }));
    if (step === 2) {
      if (cur.kind === 'aadhaar' && aadhaarEmpty(cur)) return setCur((r) => ({ ...r, kind: 'other', skipped: true }));
      if (!curOk) return setTried((t) => ({ ...t, [`r${box}`]: true }));
      return lastBox ? go(3) : go(2, box + 1);
    }
    void submit();
  }

  function back() {
    if (step === 3) return go(2, applyAll ? 0 : n - 1);
    if (step === 2 && box > 0) return go(2, box - 1);
    go(1, 0);
  }

  const switchKind = () => setCur((r) => ({ ...r, kind: r.kind === 'aadhaar' ? 'other' : 'aadhaar', skipped: false }));

  async function submit() {
    if (!confirmed) return setTried((t) => ({ ...t, 3: true }));
    setSending(true);
    setError(null);
    const side = (f: DocForm): KycSide => ({
      number: f.number, name: f.name, address: f.address, mobileCode: f.code, mobileNumber: f.mobile,
      front: f.front?.blob, back: f.back?.blob,
    });
    const receivers: KycReceiver[] = rs.map((r) =>
      r.kind === 'aadhaar'
        ? { ...side(r.ad), kind: 'aadhaar', pin: r.ad.pin, area: r.ad.area, district: r.ad.district, state: r.ad.state, country: 'India' }
        : { ...side(r.ot), kind: 'other', docName: r.ot.docName, country: r.ot.country },
    );
    try {
      await submitKyc(token, side(iq), receivers, applyAll || n === 1);
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  // Keyboard: Enter / ⌘-Enter = Continue, Esc = Back, O = Aadhaar ↔ other ID, A = for all boxes, ? = the list.
  const keys = useRef({ next, back, switchKind, all: () => {}, help: () => {} });
  keys.current = {
    next,
    back,
    switchKind: () => step === 2 && switchKind(),
    all: () => step === 2 && box === 0 && n > 1 && setApplyAll((v) => !v),
    help: () => setKeysOpen((v) => !v),
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || sending) return;
      const t = e.target as HTMLElement;
      if (e.key === 'Escape' && typing(t)) return t.blur();
      if (e.key === 'Escape' && keysOpen) return setKeysOpen(false);
      if (windowOpen()) return;
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        return keys.current.next();
      }
      // Enter in a one-line box or on the page — not in the address box, not on a button.
      if (e.key === 'Enter' && !e.shiftKey && !e.altKey && t.tagName !== 'TEXTAREA' && t.tagName !== 'BUTTON' && t.tagName !== 'A') {
        e.preventDefault();
        return keys.current.next();
      }
      if (typing(t) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape') return keys.current.back();
      if (e.key === '?') return keys.current.help();
      if (plainKey(e, 'o')) return keys.current.switchKind();
      if (plainKey(e, 'a')) return keys.current.all();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sending, keysOpen]);

  // ── states without the form ──
  const frame = (body: React.ReactNode) => (
    <div className={styles.app}>
      <header className={styles.top}>
        <img src={logo} alt="Sahari Cargo" className={styles.logo} />
      </header>
      <main className={styles.center}>{body}</main>
    </div>
  );
  if (badLink) return frame(<div className={styles.notice}><h2>{badLink}</h2><p>Please message us on WhatsApp or call {HELP}.</p></div>);
  if (!info) return frame(<p className={styles.loading}>Loading…</p>);
  if (info.state === 'expired')
    return frame(<div className={styles.notice}><div className={styles.big}>⌛</div><h2>This link has expired</h2><p>Please reply on WhatsApp or call {HELP} and we will send a new one.</p></div>);
  if (done || info.state === 'submitted')
    return frame(
      <div className={styles.notice}>
        <div className={styles.ring}><span><Tick size={30} /></span></div>
        <h2>KYC submitted</h2>
        <p>
          Thank you. We received the documents for{' '}
          {info.boxNumbers?.length ? (
            <b>{info.boxNumbers.length === 1 ? 'Box No.' : 'Boxes'} {info.boxNumbers.join(', ')}</b>
          ) : (
            <b>{info.awbNumber}</b>
          )}
          . Our team will check them and message you on WhatsApp if anything is missing.
        </p>
      </div>,
    );

  const stepClass = (s: number) => `${styles.st} ${step === s ? styles.on : ''} ${step > s ? styles.ok : ''}`;
  const tips = <p className={styles.tips}><span>☀️ Good light</span><span>✋ Whole card in view</span><span>🔍 Text readable</span></p>;
  const ad = cur.ad;
  const ot = cur.ot;
  const missing = !!tried[triedKey];
  const k = (field: string) => `r${box}.${cur.kind}.${field}`;
  const placeOf = (r: Receiver) =>
    r.kind === 'aadhaar' ? [r.ad.area, r.ad.district, r.ad.state, r.ad.pin].filter(Boolean).join(', ') : r.ot.country;

  return (
    <div className={styles.app}>
      <header className={styles.top}>
        <img src={logo} alt="Sahari Cargo" className={styles.logo} />
        <div className={styles.ttl}>
          <h1>Complete your KYC</h1>
          <p>Customs needs the ID of the sender and the receiver.</p>
        </div>
        <ol className={styles.steps}>
          <li className={stepClass(1)}><i>{step > 1 ? <Tick /> : 1}</i><span>Your Iqama</span></li>
          <li className={`${styles.line} ${step > 1 ? styles.lineOk : ''}`} aria-hidden />
          <li className={stepClass(2)}><i>{step > 2 ? <Tick /> : 2}</i><span>Receiver's ID</span></li>
          <li className={`${styles.line} ${step > 2 ? styles.lineOk : ''}`} aria-hidden />
          <li className={stepClass(3)}><i>3</i><span>Check &amp; submit</span></li>
        </ol>
        <span className={styles.secure}>🔒 Secure</span>
      </header>

      <div className={styles.ship}>
        <div><span>Shipment</span><b>{info.awbNumber}</b></div>
        <div><span>Sender</span><b>{info.senderName}</b></div>
        <div><span>Receiver</span><b>{info.receiverName}</b></div>
        <div>
          <span>{info.boxes === 1 ? 'Box No.' : `Boxes (${info.boxes})`}</span>
          <b>
            {(info.boxNumbers ?? []).slice(0, 3).join(', ') || info.boxes}
            {(info.boxNumbers ?? []).length > 3 ? ` +${info.boxNumbers.length - 3} more` : ''}
          </b>
        </div>
        <div className={styles.why}>🔒 Used only for customs clearance</div>
      </div>

      <main className={styles.main}>
        {step === 1 && (
          <div className={styles.page}>
            <section className={styles.panel}>
              <div className={styles.ph}>
                <div className={styles.tile}><CardIcon /></div>
                <div><h2>Your Iqama</h2><p>Front photo is needed · back is optional</p></div>
                <span className={`${styles.pill} ${iqOk ? styles.pillOk : ''}`}>{iqOk ? 'Ready' : 'Needed'}</span>
              </div>
              <div className={styles.ups}>
                <PhotoSlot label="Front side" kind="iqama" front needed photo={iq.front} missing={missing} onPhoto={(p) => setIq((f) => ({ ...f, front: p }))} />
                <PhotoSlot label="Back side" kind="iqama" front={false} needed={false} photo={iq.back} missing={missing} onPhoto={(p) => setIq((f) => ({ ...f, back: p }))} />
              </div>
              {tips}
            </section>
            <section className={styles.panel}>
              <div className={styles.ph}><div><h2>Details on the Iqama</h2><p>Only the Iqama number is needed — the rest helps us, fill what you can</p></div></div>
              <div className={styles.form}>
                <Field label="Iqama number" need check={iqChecks.number} show={show('iq.number')} hint="10 digits, on the front of the Iqama (see the sample)">
                  <input inputMode="numeric" value={iq.number} onChange={(e) => setIq((f) => ({ ...f, number: formatIqama(e.target.value) }))} onBlur={blur('iq.number')} placeholder="2XXX XXX XXX — as on the card" />
                </Field>
                <Field label="Name as on Iqama" check={iqChecks.name} show={show('iq.name')}>
                  <input value={iq.name} onChange={(e) => setIq((f) => ({ ...f, name: formatName(e.target.value) }))} onBlur={blur('iq.name')} />
                </Field>
                <Field label="Address in Saudi Arabia" check={iqChecks.address} show={show('iq.address')}>
                  <textarea rows={5} value={iq.address} onChange={(e) => setIq((f) => ({ ...f, address: e.target.value }))} onBlur={blur('iq.address')} />
                </Field>
                <PhoneField code={iq.code} value={iq.mobile} check={iqChecks.mobile} show={show('iq.mobile')}
                  onCode={(c) => setIq((f) => ({ ...f, code: c, mobile: formatMobile(c, f.mobile) }))}
                  onChange={(v) => setIq((f) => ({ ...f, mobile: v }))} onBlur={blur('iq.mobile')} />
              </div>
              <p className={styles.note}>💡 Next you'll add the receiver's Aadhaar — or their passport if they live outside India.</p>
            </section>
          </div>
        )}

        {step === 2 && (
          <div className={`${styles.page} ${n > 1 ? styles.withBoxes : ''}`}>
            {n > 1 && (
              <div className={styles.who}>
                <span className={styles.q}>Receiver for each box</span>
                <span className={styles.bxs}>
                  {rs.map((r, i) => {
                    const here = i === box;
                    const same = applyAll && i > 0;
                    const ok = !here && !same && receiverOk(r);
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`${styles.bx} ${here ? styles.bxCur : ''} ${ok ? styles.bxDone : ''}`}
                        disabled={here || same || !reachable(i)}
                        onClick={() => go(2, i)}
                      >
                        <b>{boxName(i)}</b>
                        <span>{here ? '● Filling' : same ? `= same as ${boxName(0)}` : ok ? '✓ Added' : 'Not added'}</span>
                      </button>
                    );
                  })}
                </span>
                {box === 0 && (
                  <label className={`${styles.all} ${applyAll ? styles.allOn : ''}`}>
                    <input type="checkbox" checked={applyAll} onChange={(e) => setApplyAll(e.target.checked)} />
                    <span>
                      <b>Use this receiver for all my boxes ({n}) <Kbd k="A" /></b>
                      <small>{applyAll ? 'Every box gets the same receiver — no need to fill them again' : 'Tick this if every box goes to the same person'}</small>
                    </span>
                  </label>
                )}
              </div>
            )}

            {cur.kind === 'aadhaar' ? (
              <>
                <section className={styles.panel}>
                  <div className={styles.ph}>
                    <div className={styles.tile}><CardIcon /></div>
                    <div><h2>Receiver's Aadhaar</h2><p>Both sides needed</p></div>
                    <button type="button" className={styles.swap} onClick={switchKind}>No Aadhaar? Use another ID <Kbd k="O" /></button>
                  </div>
                  <div className={styles.ups}>
                    <PhotoSlot key={`${box}-af`} label="Front side" kind="aadhaar" front needed photo={ad.front} missing={missing} onPhoto={(p) => setAd((f) => ({ ...f, front: p }))} />
                    <PhotoSlot key={`${box}-ab`} label="Back side" kind="aadhaar" front={false} needed photo={ad.back} missing={missing} onPhoto={(p) => setAd((f) => ({ ...f, back: p }))} />
                  </div>
                  {tips}
                </section>
                <section className={styles.panel}>
                  <div className={styles.ph}><div><h2>Details on the Aadhaar</h2><p>Only the Aadhaar number is needed — fill the rest if you can</p></div></div>
                  <div className={styles.form}>
                    <Field label="Aadhaar number" need check={adChecks.number} show={show(k('number'))} hint="12 digits, on the front of the Aadhaar (see the sample)">
                      <input inputMode="numeric" value={ad.number} onChange={(e) => setAd((f) => ({ ...f, number: formatAadhaar(e.target.value) }))} onBlur={blur(k('number'))} placeholder="XXXX XXXX XXXX" />
                    </Field>
                    <Field label="Name as on Aadhaar" check={adChecks.name} show={show(k('name'))}>
                      <input value={ad.name} onChange={(e) => setAd((f) => ({ ...f, name: formatName(e.target.value) }))} onBlur={blur(k('name'))} />
                    </Field>
                    <Field label="Address" check={adChecks.address} show={show(k('address'))}>
                      <textarea rows={5} value={ad.address} onChange={(e) => setAd((f) => ({ ...f, address: e.target.value }))} onBlur={blur(k('address'))} />
                    </Field>
                    <PhoneField code={ad.code} value={ad.mobile} check={adChecks.mobile} show={show(k('mobile'))}
                      onCode={(c) => setAd((f) => ({ ...f, code: c, mobile: formatMobile(c, f.mobile) }))}
                      onChange={(v) => setAd((f) => ({ ...f, mobile: v }))} onBlur={blur(k('mobile'))} />
                    <div className={styles.sep}>From the back of the card · 🇮🇳 India</div>
                    <Field label="PIN code" check={adChecks.pin} show={show(k('pin'))} hint="Type the PIN and the rest fills in — no PIN? choose below">
                      <input inputMode="numeric" value={ad.pin} placeholder="6XXXXX" onChange={(e) => setAd((f) => ({ ...f, pin: formatPin(e.target.value) }))} onBlur={blur(k('pin'))} />
                    </Field>
                    <Field label="State" show={false}>
                      <SearchSelect
                        value={ad.state}
                        options={STATE_OPTIONS}
                        onChange={(v) => setAd((f) => ({ ...f, state: v, district: (INDIA[v] ?? []).includes(f.district) ? f.district : '' }))}
                        placeholder="Choose state"
                        searchPlaceholder="Search state"
                        ariaLabel="State"
                      />
                    </Field>
                    <Field label="District" show={false}>
                      <SearchSelect
                        value={ad.district}
                        options={(INDIA[ad.state] ?? []).map((d) => ({ value: d, label: d }))}
                        onChange={(v) => setAd((f) => ({ ...f, district: v }))}
                        placeholder={ad.state ? 'Choose district' : 'Choose the state first'}
                        searchPlaceholder="Search or type district"
                        disabled={!ad.state}
                        allowCustom
                        ariaLabel="District"
                      />
                    </Field>
                    <Field label="Area / locality" show={false} hint={areas.length ? `${areas.length} areas for this PIN` : 'Type your area if it is not listed'}>
                      <SearchSelect
                        value={ad.area}
                        options={areas.map((a) => ({ value: a, label: a }))}
                        onChange={(v) => setAd((f) => ({ ...f, area: v }))}
                        placeholder={areas.length ? 'Choose your area' : 'Type your area'}
                        searchPlaceholder={areas.length ? 'Search area' : 'Type your area'}
                        disabled={!ad.district}
                        allowCustom
                        ariaLabel="Area / locality"
                      />
                    </Field>
                  </div>
                </section>
              </>
            ) : (
              <>
                <section className={styles.panel}>
                  <div className={styles.ph}>
                    <div className={styles.tile}><CardIcon /></div>
                    <div><h2>Receiver's other ID</h2><p>Passport or their country's ID card</p></div>
                    <button type="button" className={styles.swap} onClick={switchKind}>← Use Aadhaar instead <Kbd k="O" /></button>
                  </div>
                  {cur.skipped && (
                    <p className={styles.skip}>No Aadhaar added for this box — add the receiver's passport or other ID instead. One of the two is needed.</p>
                  )}
                  <div className={styles.ups}>
                    <PhotoSlot key={`${box}-of`} label="Front side" kind="other" front needed photo={ot.front} missing={missing} onPhoto={(p) => setOt((f) => ({ ...f, front: p }))} />
                    <PhotoSlot key={`${box}-ob`} label="Back side" kind="other" front={false} needed={false} photo={ot.back} missing={missing} onPhoto={(p) => setOt((f) => ({ ...f, back: p }))} />
                  </div>
                  {tips}
                </section>
                <section className={styles.panel}>
                  <div className={styles.ph}><div><h2>Details on the ID</h2><p>Which ID and its number are needed — fill the rest if you can</p></div></div>
                  <div className={styles.form}>
                    <Field label="Which ID?" need check={otChecks.docName} show={show(k('docName'))}>
                      <SearchSelect
                        value={ot.docName}
                        options={ID_OPTIONS}
                        onChange={(v) => setOt((f) => ({ ...f, docName: v }))}
                        placeholder="Choose the ID"
                        searchPlaceholder="Search or type the ID name"
                        allowCustom
                        ariaLabel="Which ID"
                        onBlur={blur(k('docName'))}
                      />
                    </Field>
                    <Field label="ID number" need check={otChecks.number} show={show(k('number'))} hint="Letters and digits, as printed">
                      <input value={ot.number} onChange={(e) => setOt((f) => ({ ...f, number: formatOtherNumber(e.target.value) }))} onBlur={blur(k('number'))} placeholder="As printed on the ID" />
                    </Field>
                    <Field label="Country" show={false}>
                      <SearchSelect
                        value={ot.country}
                        options={COUNTRY_OPTIONS}
                        onChange={(v) => setOt((f) => ({ ...f, country: v }))}
                        placeholder="Choose country"
                        searchPlaceholder="Search country"
                        ariaLabel="Country"
                      />
                    </Field>
                    <Field label="Name as on the ID" check={otChecks.name} show={show(k('name'))}>
                      <input value={ot.name} onChange={(e) => setOt((f) => ({ ...f, name: formatName(e.target.value) }))} onBlur={blur(k('name'))} />
                    </Field>
                    <Field label="Address" check={otChecks.address} show={show(k('address'))}>
                      <textarea rows={5} value={ot.address} onChange={(e) => setOt((f) => ({ ...f, address: e.target.value }))} onBlur={blur(k('address'))} />
                    </Field>
                    <PhoneField code={ot.code} value={ot.mobile} check={otChecks.mobile} show={show(k('mobile'))}
                      onCode={(c) => setOt((f) => ({ ...f, code: c, mobile: formatMobile(c, f.mobile) }))}
                      onChange={(v) => setOt((f) => ({ ...f, mobile: v }))} onBlur={blur(k('mobile'))} />
                  </div>
                </section>
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className={`${styles.page} ${styles.review}`}>
            <section className={styles.panel}>
              <div className={styles.ph}>
                <div className={styles.tile}><CardIcon /></div>
                <div><h2>Your Iqama</h2><p>Sender{n > 1 ? ' · same for all boxes' : ''}</p></div>
                <button type="button" className={styles.edit} onClick={() => go(1, 0)}>Edit</button>
              </div>
              <div className={styles.revPh}>
                {([['Front side', iq.front], ['Back side', iq.back]] as const).map(([l, p]) => (
                  <figure key={l}>
                    {p ? <img src={p.url} alt={l} /> : <span className={styles.noPhoto} />}
                    <figcaption className={p ? '' : styles.capSoft}>{p ? <Tick size={12} /> : null}{p ? l : `${l} not added (optional)`}</figcaption>
                  </figure>
                ))}
              </div>
              <dl className={styles.dl}>
                {[['Iqama number', iq.number], ['Name', iq.name], ['Address', iq.address], ['Mobile', iq.mobile && `+${iq.code} ${iq.mobile}`]].map(([key, v]) => (
                  <div key={key}><dt>{key}</dt><dd>{v || '—'}</dd></div>
                ))}
              </dl>
            </section>
            <section className={styles.panel}>
              <div className={styles.ph}>
                <div className={styles.tile}><CardIcon /></div>
                <div>
                  <h2>{applyAll || n === 1 ? 'Receiver' : 'Receivers'}</h2>
                  <p>{n === 1 ? 'Receiver of the box' : applyAll ? `The same receiver for all ${n} boxes` : 'A receiver for each box'}</p>
                </div>
              </div>
              {(applyAll ? rs.slice(0, 1) : rs).map((r, i) => {
                const f = r.kind === 'aadhaar' ? r.ad : r.ot;
                return (
                  <div key={i} className={styles.rvBox}>
                    <div className={styles.mini}>
                      {[f.front, f.back].map((p, j) => (p ? <img key={j} src={p.url} alt="" /> : <span key={j} />))}
                    </div>
                    <div>
                      <h4>
                        {n > 1 && <>{applyAll ? `All ${n} boxes` : boxName(i)} · </>}
                        {f.name || 'Receiver'}{' '}
                        <span className={`${styles.kind} ${r.kind === 'aadhaar' ? styles.kindIn : ''}`}>{r.kind === 'aadhaar' ? 'Aadhaar' : r.ot.docName}</span>
                      </h4>
                      <p>{[r.kind === 'aadhaar' ? masked(r.ad.number) : `No. ${r.ot.number}`, placeOf(r)].filter(Boolean).join(' · ')}</p>
                    </div>
                    <button type="button" className={styles.edit} onClick={() => go(2, i)}>Edit</button>
                  </div>
                );
              })}
            </section>
            <label className={`${styles.confirm} ${tried[3] && !confirmed ? styles.confirmBad : ''}`}>
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              I confirm these are the documents of the sender and the {applyAll || n === 1 ? 'receiver' : 'receivers'} of this shipment.
            </label>
          </div>
        )}
      </main>

      {keysOpen && (
        <div className={styles.keys} role="note" aria-label="Keyboard shortcuts">
          <div className={styles.keysHead}>
            <b>Keyboard shortcuts</b>
            <button type="button" onClick={() => setKeysOpen(false)} aria-label="Close">✕</button>
          </div>
          <dl>
            {SHORTCUTS.map(([key, what]) => (
              <div key={key}><dt><Kbd k={key} /></dt><dd>{what}</dd></div>
            ))}
          </dl>
        </div>
      )}

      <footer className={styles.bar}>
        <span className={styles.help}>📞 Need help? WhatsApp or call {HELP}</span>
        <button type="button" className={styles.keysBtn} onClick={() => setKeysOpen((v) => !v)}>⌨ Shortcuts <Kbd k="?" /></button>
        {error && <span className={styles.err}>{error}</span>}
        {step > 1 && (
          <button type="button" className={`${styles.btn} ${styles.gh}`} onClick={back} disabled={sending}>
            Back <Kbd k="Esc" />
          </button>
        )}
        <button type="button" className={styles.btn} onClick={next} disabled={sending}>
          {step === 3 ? (sending ? 'Submitting…' : 'Submit KYC') : step === 2 && !lastBox ? 'Next box →' : 'Continue'} <Kbd k="Enter" />
        </button>
      </footer>
    </div>
  );
};

export default Kyc;
